import { createContext, useContext, useState, ReactNode } from 'react'

interface Plugin {
  pluginId: string
  pluginName: string
  version: string
  commands: Array<{ name: string; description: string }>
}

interface PluginManagerContextType {
  plugins: Plugin[]
  registerPlugin: (plugin: Plugin) => void
  unregisterPlugin: (pluginId: string) => void
  executeCommand: (pluginId: string, command: string, params: Record<string, any>) => Promise<any>
}

const PluginManagerContext = createContext<PluginManagerContextType | undefined>(undefined)

export function PluginManager({ children }: { children: ReactNode }) {
  const [plugins, setPlugins] = useState<Plugin[]>([])
  
  const registerPlugin = (plugin: Plugin) => {
    setPlugins(prev => [...prev.filter(p => p.pluginId !== plugin.pluginId), plugin])
  }
  
  const unregisterPlugin = (pluginId: string) => {
    setPlugins(prev => prev.filter(p => p.pluginId !== pluginId))
  }
  
  const executeCommand = async (pluginId: string, command: string, params: Record<string, any>) => {
    // In a real implementation, this would call the backend
    console.log(`Executing ${command} on ${pluginId}`, params)
    return { success: true }
  }
  
  return (
    <PluginManagerContext.Provider value={{ plugins, registerPlugin, unregisterPlugin, executeCommand }}>
      {children}
    </PluginManagerContext.Provider>
  )
}

export function usePlugins() {
  const context = useContext(PluginManagerContext)
  if (!context) {
    throw new Error('usePlugins must be used within PluginManager')
  }
  return context
}